import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { Link, useHistory } from "react-router-dom";
import { Button, Message } from "semantic-ui-react";
import {
  getAuthState,
  getAuthClaims,
  getAuthOptions,
  FormBuilder,
  Form,
  ErrorMessage,
} from "@redriver/cinnamon";
import { SystemRoutes } from "constants/routes";
import { requestLogin, requestMagicLink } from "./actions";
import { EmailVerificationResender } from "../EmailVerification";
import AzureSsoSignInButton from "./AzureSsoSignInButton";

const Login = ({ loggedIn, emailVerified, useEmailVerification }) => {
  const [usesMicrosoftSignIn, setUsesMicrosoftSignIn] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const history = useHistory();

  // clicking "Sign in with Microsoft" navigates away to login.microsoftonline.com,
  // which leaves this page eligible for the browser's back-forward cache - if the
  // user hits back before finishing (or after finishing, from wherever they land),
  // the browser can restore this exact page from memory frozen mid-redirect (still
  // showing the button's spinner, still with a stale pre-login loggedIn value)
  // without re-running any of our checks. Force a real reload so it re-evaluates
  // fresh instead of resurrecting that frozen snapshot.
  useEffect(() => {
    const handlePageShow = (event) => {
      if (event.persisted) {
        window.location.reload();
      }
    };
    window.addEventListener("pageshow", handlePageShow);
    return () => window.removeEventListener("pageshow", handlePageShow);
  }, []);

  if (loggedIn) {
    if (emailVerified || !useEmailVerification) {
      return <p>You have been successfully signed in</p>;
    }

    return (
      <div>
        <p>Your email address has not been verified</p>
        <EmailVerificationResender />
      </div>
    );
  }

  if (usesMicrosoftSignIn) {
    return (
      <React.Fragment>
        <Message warning>
          This account signs in with Microsoft. Use the &quot;Sign in with
          Microsoft&quot; button below instead of a password.
        </Message>
        <AzureSsoSignInButton />
      </React.Fragment>
    );
  }

  if (magicLinkSent) {
    return (
      <div>
        <Message positive>
          <p>
            If an account exists for {userEmail}, you will receive a sign-in
            link to that address. It will expire in 20 minutes.
          </p>
        </Message>
        <p>
          Return to{" "}
          <button
            type="button"
            className="link-button"
            onClick={() => {
              setMagicLinkSent(false);
              setShowPassword(false);
            }}
          >
            Log In
          </button>
        </p>
      </div>
    );
  }

  return (
    <React.Fragment>
      <FormBuilder
        submitAction={showPassword ? requestLogin : requestMagicLink}
        onChange={(value) => setUserEmail(value.email)}
        onSubmitted={(response) => {
          if (response?.usesMicrosoftSignIn) {
            setUsesMicrosoftSignIn(true);
            return;
          }

          if (!showPassword) {
            setMagicLinkSent(true);
            return;
          }

          const { secondFactor, tokens } = response ?? {};
          if ((secondFactor?.availableTypes ?? []).length > 0) {
            const {
              registeredTypes,
              availableTypes,
              recoveryTypes,
              token,
              required,
            } = secondFactor;
            history.push(SystemRoutes.Authenticate, {
              registeredTypes,
              availableTypes,
              recoveryTypes,
              token,
              loginTokens: tokens,
              enforceMfa: required,
              userEmail,
            });
          }
        }}
        renderSubmitting={false}
        renderError={false}
        renderForm={(
          formProps,
          { submitting, slowSubmitting, error },
          events
        ) => (
          <Form
            {...formProps}
            className="public-form public-login-form"
            autoComplete="on"
          >
            <strong>Welcome to the Maples Teesdale LLP Data Room</strong>
            <p>
              This is our secure client extranet service for file sharing,
              collaboration and communication on your projects.
            </p>
            <h1 className="big-header">Log in</h1>
            <Form.Email
              field="email"
              placeholder="Email"
              name="email"
              disabled={submitting && slowSubmitting}
              autoFocus
              required
              fluid
              icon="at"
              autoComplete="username"
            />
            {showPassword && (
              <Form.Password
                field="password"
                placeholder="Password"
                name="password"
                disabled={submitting && slowSubmitting}
                required
                fluid
                toggleVisibility
                autoComplete="current-password"
              />
            )}

            {showPassword ? (
              <React.Fragment>
                <Button
                  className="confirm-btn"
                  onClick={events.onSubmit}
                  disabled={submitting}
                  loading={submitting && slowSubmitting}
                  fluid
                >
                  Log In
                </Button>
                <p>
                  Back to{" "}
                  <button
                    type="button"
                    className="link-button"
                    onClick={() => setShowPassword(false)}
                  >
                    login options
                  </button>
                </p>
              </React.Fragment>
            ) : (
              <div className="stacked-buttons">
                <Button
                  className="confirm-btn"
                  onClick={() => setShowPassword(true)}
                  fluid
                >
                  Continue with password
                </Button>
                <Button
                  className="magic-link-btn"
                  onClick={events.onSubmit}
                  disabled={submitting}
                  loading={submitting && slowSubmitting}
                  fluid
                >
                  Email me a sign-in link
                </Button>
              </div>
            )}

            <ErrorMessage header="Sign In Error" error={error} omitCodes />
          </Form>
        )}
      />
      <AzureSsoSignInButton />
      <div className="disclaimer-box">
        <p>
          By accessing and using the Maples Teesdale LLP Data Room you:
          <ul>
            <li>
              Agree to be bound by our{" "}
              <a href="https://www.maplesteesdale.co.uk/data-room-terms-of-use/">
                terms of use
              </a>{" "}
              and any additional rules which are set out on the site from time
              to time; and
            </li>
            <li>
              Consent to any personal data that you provide being held on the
              system.
            </li>
          </ul>
        </p>
        <p>
          This secure Data Room has been established in connection with the
          specific property and transaction to which the title refers, and
          contains information provided solely for the purposes of this
          transaction. The information contained in the Data Room is
          confidential, and access is restricted and monitored. Do not attempt
          to use this site unless you are an authorised user and have a valid
          reason to access it. Unauthorised use, disclosure, or distribution of
          any information contained in the Data Room is strictly prohibited.
        </p>
        <p>
          If you require technical support, or believe you have received a link
          to this site in error, please email{" "}
          <a href="mailto:extranetsupport@maplesteesdale.co.uk">
            extranetsupport@maplesteesdale.co.uk
          </a>
          .
        </p>
        {showPassword && (
          <p>
            <span>
              Forgot Password?{" "}
              <Link to={SystemRoutes.ForgottenPassword}>Reset it</Link>
            </span>
          </p>
        )}
      </div>
    </React.Fragment>
  );
};

const mapStateToProps = (state) => {
  const { loggedIn } = getAuthState(state);
  const { emailVerified } = getAuthClaims(state);
  const { useEmailVerification } = getAuthOptions();
  return {
    loggedIn,
    emailVerified,
    useEmailVerification,
  };
};

export default connect(mapStateToProps)(Login);
